import React, { useState, useEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";
import Card from "../../../components/Card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dropdown } from "primereact/dropdown";
import { Dialog } from "primereact/dialog";
import { FiEdit, FiPlus, FiTrash2 } from "react-icons/fi";
import { ROUTES, USER_ROLES, MODULE_ACL } from '../../../utilities/constant';
import { useNavigate } from "react-router-dom";
import Loader  from '../../../components/partials/components/loader';
import {
    API_MANAGE_GROUP_LISTING,
    API_MANAGE_GROUP_UPDATE,
    API_MANAGE_GROUP_DELETE,
} from "../../../service/url";
import { getUser } from "../../../utilities/setting";
import exportData from '../../../utilities/export';
import { getApiCall } from "../../../service/getApiCall";
import { postApiCall } from "../../../service/postApiCall";
import { formatDateTime } from "../../../utilities/helpers";
import { toast } from 'react-toastify';

export default function GroupList() {
    const navigate = useNavigate();
    const [groups, setGroups] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [rows, setRows] = useState(5);
    const [loading, setLoading] = useState(false);
    const [isSuperAdmin, checkIsSuperAdmin] = useState(false);

    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editGroup, setEditGroup] = useState({ id: null, name: "" });

    // ✅ Delete modal state
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
    const [toastProps, setToastProps] = useState(null);
    const [moduleOperations, setModuleOperations] = useState({});


    // ✅ Fetch groups
    const fetchGroups = async () => {
        setLoading(true);
        try {
            const data = await getApiCall(API_MANAGE_GROUP_LISTING, {});
            setGroups(data || []);
        } catch (error) {
            console.error("Error fetching groups:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGroups();
    }, []);

    useEffect(() => {
      getUser().then((user) => {
        if (user) {
          if (user.role === USER_ROLES.SUPERADMIN) {
            checkIsSuperAdmin(true);
          }
          if (user?.acl_settings) {
            setModuleOperations(JSON.parse(user.acl_settings.module_operations));
          }
        }
      });
    }, []);

    // ✅ Filter for search
    const filteredGroups = groups.filter((group) => {
        const groupName = group?.group_name || "";
        return groupName.toLowerCase().includes(searchValue.toLowerCase());
    });

    // ✅ Checkbox logic
    const handleCheckboxToggle = (groupId) => {
        const isSelected = selectedGroups.includes(groupId);
        setSelectedGroups(
            isSelected
                ? selectedGroups.filter((id) => id !== groupId)
                : [...selectedGroups, groupId]
        );
    };

    const handleSelectAll = () => {
        if (selectedGroups.length === groups.length) {
            setSelectedGroups([]);
        } else {
            setSelectedGroups(groups.map((group) => group.group_id));
        }
    };

    // ✅ Open delete modal for single delete
    const confirmDelete = (id) => {
        console.log("CONFI<R ", id);
        setGroupToDelete(id);
        setBulkDeleteMode(false);
        setDeleteModalVisible(true);
    };

    // ✅ Open delete modal for bulk delete
    const confirmBulkDelete = () => {
        setBulkDeleteMode(true);
        setDeleteModalVisible(true);
    };

    // ✅ Execute delete
    const executeDelete = async () => {
        try {
            if (bulkDeleteMode) {
                const response = await Promise.all(
                    selectedGroups.map((id) =>
                        postApiCall(API_MANAGE_GROUP_DELETE, { id })
                    )
                );

                console.log(response);

                if (response) {
                    toast.success(response.message || "Groups deleted successfully");
                }
                setGroups(groups.filter((g) => !selectedGroups.includes(g.id)));
                setSelectedGroups([]);
            } else {
                const response = await postApiCall(API_MANAGE_GROUP_DELETE, { id: groupToDelete });
                if (response) {
                    toast.success(response.message || "Group deleted successfully");

                }
                else {
                    toast.error(response.message || "Error deleting group");
                }
                setGroups(groups.filter((g) => g.id !== groupToDelete));
                setSelectedGroups(selectedGroups.filter((id) => id !== groupToDelete));
            }
        } catch (error) {
            toast.error(error.message || error || "Error deleting group(s)");
        } finally {
            setDeleteModalVisible(false);
            setGroupToDelete(null);
            setBulkDeleteMode(false);
            await fetchGroups();
        }
    };

    // ✅ Update group
    const handleUpdate = async () => {
        try {
            const response = await postApiCall(API_MANAGE_GROUP_UPDATE(editGroup.id), {
                group_name: editGroup.name,
            });
            if (response) {
                toast.success(response.message || "Group updated successfully");
            } else {
                toast.error(response?.message || "Update group failed");
            }
            setEditModalVisible(false);
            await fetchGroups();
        } catch (error) {
            toast.error(error.message || error || "Error Updating Group");
        }
    };

    // ✅ Action buttons
    const actionBodyTemplate = (rowData) => (
        <div style={{ display: "flex", gap: "10px" }}>
            {(isSuperAdmin || moduleOperations?.[MODULE_ACL.GROUPS]?.edit) && (
                <FiEdit
                    style={{ color: "#6c757d", cursor: "pointer", fontSize: "18px" }}
                    onClick={() => {
                        setEditGroup({ id: rowData.group_id, name: rowData.group_name });
                        setEditModalVisible(true);
                    }}
                />
            )}

            {(isSuperAdmin || moduleOperations?.[MODULE_ACL.GROUPS]?.delete) && (
                <FiTrash2
                    style={{ color: "#dc3545", cursor: "pointer", fontSize: "18px" }}
                    onClick={() => confirmDelete(rowData.group_id)}
                />
            )}

        </div>
    );

    // ✅ Row highlight
    const rowClassName = (data) => ({
        "highlighted-row": selectedGroups.includes(data.id),
    });

    // ✅ Modal footer for edit
    const renderEditFooter = () => (
        <div>
            <Button
                label="Cancel"
                className="p-button-text"
                onClick={() => setEditModalVisible(false)}
            />
            <Button
                label="Update"
                className="p-button-primary"
                onClick={handleUpdate}
            />
        </div>
    );
    const [buttonDisable, setButtonDisable] = useState(false);

    const [showToast, setShowToast] = useState(false);

    const handleExport = async () => {
        setButtonDisable(true); 
          await exportData('Groups', setToastProps, setShowToast);
          setButtonDisable(false); 
        };

    if (loading) {
              return <Loader />
           }

    return (
        <div>
            <Row>
                <Col sm="12">
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <div className="header-title">
                                <h4 className="card-title">Group List</h4>
                            </div>
                            <div style={{display: "flex", alignItems: "flex-start", justifyContent: "flex-start"}}>
                            { (isSuperAdmin || moduleOperations?.[MODULE_ACL.GROUPS]?.add) && (<button className="btn btn-primary ml-auto" onClick={() => navigate(`/${ROUTES.MANAGE_GROUPS.CREATE}`)}>
                                <FiPlus className="me-1" />
                                Create Group</button>) }
                            {groups.length > 0 && 
                              <Button 
                                label="Export Data" 
                                icon="pi pi-file-export"
                                onClick={handleExport} 
                                className="p-button-active p-button-sm" 
                                style={{ marginBottom: '10px', marginLeft: '10px' }} 
                                disabled={buttonDisable} 
                              >
                                &nbsp;
                                {buttonDisable && (
                                  <span className="spinner-border spinner-border-sm ml-2" role="status" aria-hidden="true"></span>
                                )}
                              </Button>
                            }
                            </div>
                        </Card.Header>

                        <Card.Body className="px-0">
                            <div className="table-responsive border-bottom my-3">
                                {/* ✅ Controls row */}
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "10px",
                                        padding: "10px",
                                    }}
                                >
                                    {((isSuperAdmin || moduleOperations?.[MODULE_ACL.GROUPS]?.delete)) && (
                                        <Button
                                            label="Delete Selected"
                                            className="p-button-danger p-button-sm"
                                            disabled={selectedGroups.length === 0}
                                            onClick={confirmBulkDelete}
                                        />
                                    )}
                                    <Form.Group>
                                        <Form.Control
                                            style={{
                                                marginLeft: "10px",
                                                height: "45px",
                                                border: "1px solid #ced4da",
                                                fontSize: "15px",
                                            }}
                                            placeholder="Search by Group Name..."
                                            type="text"
                                            value={searchValue}
                                            onChange={(e) => setSearchValue(e.target.value)}
                                        />
                                    </Form.Group>

                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "5px",
                                            marginLeft: "20px",
                                        }}
                                    >
                                        <span>Show</span>
                                        <Dropdown
                                            value={rows}
                                            options={[
                                                { label: "5", value: 5 },
                                                { label: "10", value: 10 },
                                                { label: "20", value: 20 },
                                                { label: "50", value: 50 },
                                            ]}
                                            onChange={(e) => setRows(e.value)}
                                            style={{
                                                alignItems: "center",
                                                width: "100px",
                                                height: "40px",
                                            }}
                                            placeholder="Select Rows"
                                        />
                                        <span>items</span>
                                    </div>
                                </div>

                                {/* ✅ DataTable */}
                                <div className="scroll-container">
                                    <DataTable
                                        value={filteredGroups}
                                        tableStyle={{ minWidth: "50rem" }}
                                        paginator
                                        rows={rows}
                                        rowClassName={rowClassName}
                                        emptyMessage="No groups found"
                                    >
                                        <Column
                                            header={
                                                <Checkbox
                                                    checked={
                                                        selectedGroups.length === groups.length &&
                                                        groups.length > 0
                                                    }
                                                    onChange={handleSelectAll}
                                                />
                                            }
                                            headerStyle={{ width: "3rem" }}
                                            body={(rowData) => (
                                                <Checkbox
                                                    checked={selectedGroups.includes(rowData.group_id)}
                                                    onChange={() => handleCheckboxToggle(rowData.group_id)}
                                                />
                                            )}
                                        />
                                        <Column field="group_id" header="Group Id" sortable />
                                        <Column field="group_name" header="Group Name" sortable />
                                        <Column field="created_at" header="Created At" body={(rowData) => formatDateTime(rowData.created_at)} sortable />
                                        <Column header="Action" body={actionBodyTemplate} />
                                    </DataTable>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* ✅ Edit Group Modal */}
            <Dialog
                header="Update Group"
                visible={editModalVisible}
                style={{ width: "30vw" }}
                footer={renderEditFooter()}
                onHide={() => setEditModalVisible(false)}
            >
                <Form.Group>
                    <Form.Label>Group Name <span style={{ color: "red", margin: "0 4px" }}>*</span></Form.Label>
                    <Form.Control
                        type="text"
                        value={editGroup.name}
                        onChange={(e) =>
                            setEditGroup({ ...editGroup, name: e.target.value })
                        }
                    />
                </Form.Group>
            </Dialog>

            {/* ✅ Delete Confirmation Modal */}
            <Dialog
                header="Confirm Delete"
                visible={deleteModalVisible}
                style={{ width: "25vw" }}
                onHide={() => setDeleteModalVisible(false)}
                footer={
                    <div>
                        <Button
                            label="Cancel"
                            className="p-button-text"
                            onClick={() => setDeleteModalVisible(false)}
                        />
                        <Button
                            label="Delete"
                            className="p-button-danger"
                            onClick={executeDelete}
                        />
                    </div>
                }
            >
                <p>
                    Are you sure you want to delete{" "}
                    {bulkDeleteMode ? "the selected groups" : "this group"}?
                </p>
            </Dialog>
        </div>
    );
}
